import { useRef, useState } from 'react';
import { Animated, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { type StackNavigationProp } from '@react-navigation/stack';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import { CrossfadeImage } from 'src/presentation/components/molecules/CrossFadeImage';
import Colors from 'src/core/utils/Colors';
import Layout from 'src/core/utils/Layout';
import type { AuthNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Styles from 'src/core/utils/Styles';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';
import onboardingBussloo from 'assets/images/onboarding/Algemeen_beeld_Thermen_Bussloo.jpg';
import onboardingBerendonck from 'assets/images/onboarding/TBE_buitenbad_model_lowres.jpg';
import onboardingNieuweschans from 'assets/images/onboarding/TBN_noorderlicht_model_3.jpg';
import onboardingSoesterberg from 'assets/images/onboarding/TS_kruidensauna_2.jpg';
import Translations from 'src/core/utils/Translations';

const images = [onboardingBerendonck, onboardingBussloo, onboardingSoesterberg, onboardingNieuweschans] as const;

const HORIZONTAL_SHEET_PADDING = Dimens.padding18;

const OnboardingScreen = () => {
	const navigation = useNavigation<StackNavigationProp<AuthNavigation>>();

	const [index, setIndex] = useState<number>(0);
	const scrollViewRef = useRef<ScrollView>(null);
	// The pages fill the sheet's inner width, so paging, scrollTo and the index math share one number.
	const pageWidth = Layout.window.width - HORIZONTAL_SHEET_PADDING * 2;

	const steps = [
		{
			header: Translations.onboardingStep1Header,
			description: Translations.onboardingStep1Description,
		},
		{
			header: Translations.onboardingStep2Header,
			description: Translations.onboardingStep2Description,
		},
		{
			header: Translations.onboardingStep3Header,
			description: Translations.onboardingStep3Description,
		},
		{
			header: Translations.onboardingStep4Header,
			description: Translations.onboardingStep4Description,
		},
	];

	return (
		<View style={{ flex: Dimens.flex1 }}>
			<View style={Styles.screen.bottomAligned}>
				<CrossfadeImage resizeMode="cover" source={images[index] ?? images[0]} style={styles.backgroundImage} />

				<View style={styles.loginBottomSheet}>
					<ScrollView
						decelerationRate={0}
						horizontal={true}
						pagingEnabled={true}
						ref={scrollViewRef}
						showsHorizontalScrollIndicator={false}
						showsVerticalScrollIndicator={false}
						snapToAlignment={'center'}
						snapToStart={true}
						style={{ flexGrow: Dimens.flex0 }}
						onMomentumScrollEnd={({ nativeEvent }) => {
							setIndex(Math.round(nativeEvent.contentOffset.x / pageWidth));
						}}
					>
						{steps.map((step, stepIndex) => (
							<View key={stepIndex} style={{ width: pageWidth }}>
								<View style={{ paddingHorizontal: Dimens.padding18 }}>
									<Text style={styles.loginCarouselHeader}>{step.header}</Text>
									<Text style={styles.loginCarouselDescription}>{step.description}</Text>
								</View>
							</View>
						))}
					</ScrollView>

					<View style={styles.pageIndicatorRow}>
						{steps.map((step, stepIndex) => (
							<Animated.View
								key={stepIndex}
								style={[
									styles.pageIndicator,
									{
										width: index === stepIndex ? Dimens.size40 : Dimens.size20,
										backgroundColor: index === stepIndex ? Colors.gray800 : Colors.gray200,
									},
								]}
							/>
						))}
					</View>

					<View style={styles.buttonRow}>
						<PrimaryButton
							extraStyle={{ width: '60%' }}
							label={index === steps.length - 1 ? Translations.loginOrRegister : Translations.next}
							onPress={() => {
								if (index === steps.length - 1) {
									navigation.replace(Constants.screenLogin, {});
								} else {
									setIndex(index + 1);
									scrollViewRef.current?.scrollTo({
										x: pageWidth * (index + 1),
										animated: true,
									});
								}
							}}
						/>
					</View>
				</View>
			</View>
		</View>
	);
};

const styles = StyleSheet.create({
	loginBottomSheet: {
		paddingTop: Dimens.padding20,
		paddingHorizontal: HORIZONTAL_SHEET_PADDING,
		gap: Dimens.gap30,
		flexDirection: 'column',
		borderTopLeftRadius: Dimens.borderRadius5,
		borderTopRightRadius: Dimens.borderRadius5,
		backgroundColor: Colors.white,
	},
	loginCarouselHeader: {
		fontSize: Dimens.fontSize24,
		lineHeight: Dimens.lineHeight32,
		fontFamily: Fonts.ptSerif,
		color: Colors.gray800,
		marginBottom: Dimens.padding10,
		textAlign: 'center',
	},
	loginCarouselDescription: {
		fontSize: Dimens.fontSize16,
		fontFamily: Fonts.barlow,
		lineHeight: Dimens.lineHeight24,
		color: Colors.gray550,
		textAlign: 'center',
	},
	backgroundImage: {
		position: 'absolute',
		height: '70%',
		width: '100%',
		top: Dimens.padding0,
		backgroundColor: Colors.gray900,
	},
	pageIndicatorRow: {
		justifyContent: 'center',
		flexDirection: 'row',
		gap: Dimens.gap4,
	},
	pageIndicator: {
		height: Dimens.size2,
		borderRadius: Dimens.borderRadius2,
	},
	buttonRow: {
		flexDirection: 'row',
		width: '100%',
		justifyContent: 'center',
		paddingBottom: Dimens.padding60,
	},
});

export default OnboardingScreen;
